<script setup lang="ts">
import useLanguage from '@/composables/useLanguage'
import { colorHex } from '@/utils/colors'
import { PhArrowSquareUp } from '@phosphor-icons/vue'
import { computed } from 'vue'

const i18n = useLanguage('LevelArrows')

const props = defineProps<{
  level: string
  size: number
}>()

const arrowParams = computed<
  { color: keyof typeof colorMapping; orientation: 'down' | 'up' }[]
>(() => {
  const levelMapping = ['XS', 'S', 'M', 'L', 'XL']
  const isNegative = props.level.startsWith('-')
  const levelClean = props.level.replace('-', '')
  const levelIndex = levelMapping.indexOf(levelClean)

  return levelMapping.map((_, i) => ({
    color: i <= levelIndex ? (isNegative ? 'red' : 'green') : 'grey',
    orientation: isNegative ? 'down' : 'up'
  }))
})

const colorMapping = {
  grey: colorHex('slateTint5'),
  green: colorHex('success'),
  red: colorHex('error')
} as const

const ariaLabel = computed(() => {
  const isNegative = props.level.startsWith('-')
  const direction = isNegative ? i18n.value.negative : i18n.value.positive
  const count = arrowParams.value.filter(
    (arrow) => arrow.color !== 'grey'
  ).length

  return `${direction} ${count} ${i18n.value.outOf} 5 ${i18n.value.arrows}`
})
</script>

<template>
  <div class="flex" :aria-label="ariaLabel" role="img" tabindex="0">
    <PhArrowSquareUp
      v-for="({ color, orientation }, index) in arrowParams"
      :key="color + index"
      :size="size"
      :color="colorMapping[color]"
      weight="fill"
      :class="[{ 'rotate-180': orientation === 'down' }]"
      data-testid="level-arrow"
      aria-hidden="true"
    />
  </div>
</template>
